import { LoaderCircle, MailCheck, UserCheck } from 'lucide-react';

import { ApiError } from '@/api/client';
import { useUnverifiedUsers, useVerifyUserEmail } from '@/api/hooks/useSuperadmin';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';
import { formatDateTime } from '@/lib/utils';

export function UnverifiedUsersTab() {
  const { data, isLoading } = useUnverifiedUsers();
  const verify = useVerifyUserEmail();

  async function handleVerify(email: string) {
    try {
      await verify.mutateAsync(email);
      toast({ title: `${email} verified`, variant: 'success' });
    } catch (err) {
      toast({
        title: 'Could not verify',
        description: err instanceof ApiError ? err.message : undefined,
        variant: 'destructive',
      });
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-16" />
        ))}
      </div>
    );
  }

  const users = data ?? [];

  if (users.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-2 py-16 text-center">
          <MailCheck className="h-8 w-8 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">Everyone's verified.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">{users.length} unverified account(s)</p>
      {users.map((u) => (
        <Card key={u.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="truncate font-medium">{u.name}</p>
              <Badge variant="secondary">{u.role}</Badge>
            </div>
            <p className="truncate text-xs text-muted-foreground">
              {u.email} {u.pharmacySlug && `· ${u.pharmacySlug}`} · joined {formatDateTime(u.createdAt)}
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={() => handleVerify(u.email)} disabled={verify.isPending}>
            {verify.isPending ? <LoaderCircle className="animate-spin" /> : <UserCheck className="h-4 w-4" />}
            Mark verified
          </Button>
        </Card>
      ))}
    </div>
  );
}